import { useQuery } from '@tanstack/react-query'
import { Currency, CurrencyAmount, Price, Token } from '@uniswap/sdk-core'
import JSBI from 'jsbi'
import { useMemo } from 'react'
import { PollingInterval } from 'uniswap/src/constants/misc'
import {
  USDB_BLAST,
  USDC,
  USDC_ARBITRUM,
  USDC_AVALANCHE,
  USDC_BASE,
  USDC_BNB,
  USDC_CELO,
  USDC_OPTIMISM,
  USDC_POLYGON,
  USDC_SEPOLIA,
  USDC_SONEIUM,
  USDC_UNICHAIN,
  USDC_UNICHAIN_SEPOLIA,
  USDC_WORLD_CHAIN,
  USDC_ZKSYNC,
  USDC_ZORA,
  USDT_MONAD_TESTNET,
} from 'uniswap/src/constants/tokens'
import { UniverseChainId, isUniverseChainId } from 'uniswap/src/features/chains/types'
import { areCurrencyIdsEqual, currencyId } from 'uniswap/src/utils/currencyId'

const USDC_DEFAULT_MIN = 1_000e6
const USDC_18_DEFAULT_MIN = 1_000e18
const USDC_SONEIUM_DEFAULT_MIN = 3_0000e4

// Stablecoin amounts used when calculating spot price for a given currency.
// The amount is large enough to filter low liquidity pairs.
export const STABLECOIN_AMOUNT_OUT: Record<UniverseChainId, CurrencyAmount<Token>> = {
  [UniverseChainId.Mainnet]: CurrencyAmount.fromRawAmount(USDC, USDC_DEFAULT_MIN),
  [UniverseChainId.ArbitrumOne]: CurrencyAmount.fromRawAmount(USDC_ARBITRUM, USDC_DEFAULT_MIN),
  [UniverseChainId.Avalanche]: CurrencyAmount.fromRawAmount(USDC_AVALANCHE, USDC_DEFAULT_MIN),
  [UniverseChainId.Base]: CurrencyAmount.fromRawAmount(USDC_BASE, USDC_DEFAULT_MIN),
  [UniverseChainId.Blast]: CurrencyAmount.fromRawAmount(USDB_BLAST, USDC_18_DEFAULT_MIN),
  [UniverseChainId.Bnb]: CurrencyAmount.fromRawAmount(USDC_BNB, USDC_18_DEFAULT_MIN),
  [UniverseChainId.Celo]: CurrencyAmount.fromRawAmount(USDC_CELO, USDC_18_DEFAULT_MIN),
  [UniverseChainId.MonadTestnet]: CurrencyAmount.fromRawAmount(USDT_MONAD_TESTNET, USDC_DEFAULT_MIN),
  [UniverseChainId.Optimism]: CurrencyAmount.fromRawAmount(USDC_OPTIMISM, USDC_DEFAULT_MIN),
  [UniverseChainId.Polygon]: CurrencyAmount.fromRawAmount(USDC_POLYGON, USDC_DEFAULT_MIN),
  [UniverseChainId.Sepolia]: CurrencyAmount.fromRawAmount(USDC_SEPOLIA, USDC_DEFAULT_MIN),
  [UniverseChainId.Soneium]: CurrencyAmount.fromRawAmount(USDC_SONEIUM, USDC_SONEIUM_DEFAULT_MIN),
  [UniverseChainId.Unichain]: CurrencyAmount.fromRawAmount(USDC_UNICHAIN, USDC_DEFAULT_MIN),
  [UniverseChainId.UnichainSepolia]: CurrencyAmount.fromRawAmount(USDC_UNICHAIN_SEPOLIA, USDC_DEFAULT_MIN),
  [UniverseChainId.WorldChain]: CurrencyAmount.fromRawAmount(USDC_WORLD_CHAIN, USDC_DEFAULT_MIN),
  [UniverseChainId.Zksync]: CurrencyAmount.fromRawAmount(USDC_ZKSYNC, USDC_DEFAULT_MIN),
  [UniverseChainId.Zora]: CurrencyAmount.fromRawAmount(USDC_ZORA, USDC_DEFAULT_MIN),
}

const ZERO_EX_NATIVE_TOKEN_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
const ZERO_EX_PRICE_URL = 'https://api-v2.morphex.trade/deliswap-0x-price'
const ZERO_EX_PRICE_STALE_TIME_MS = 30_000
const ZERO_EX_PRICE_GC_TIME_MS = 5 * ZERO_EX_PRICE_STALE_TIME_MS

interface ZeroExPriceResponse {
  buyAmount?: string
  sellAmount?: string
}

function getZeroExTokenParam(currency?: Currency) {
  if (!currency) {
    return undefined
  }

  return currency.isNative ? ZERO_EX_NATIVE_TOKEN_ADDRESS : currency.wrapped.address
}

type ZeroExPriceQuote = {
  buyAmountRaw: string
  sellAmountRaw: string
}

export function useUSDCPrice(
  currency?: Currency,
  pollInterval: PollingInterval = PollingInterval.Fast,
): {
  price: Price<Currency, Currency> | undefined
  isLoading: boolean
} {
  const chainId = currency?.chainId

  const quoteAmount = isUniverseChainId(chainId) ? STABLECOIN_AMOUNT_OUT[chainId] : undefined
  const stablecoin = quoteAmount?.currency
  const sellAmountRaw = quoteAmount?.quotient.toString()

  // avoid requesting quotes for stablecoin input
  const currencyIsStablecoin = Boolean(
    stablecoin && currency && areCurrencyIdsEqual(currencyId(currency), currencyId(stablecoin)),
  )

  const sellTokenParam = stablecoin ? getZeroExTokenParam(stablecoin) : undefined
  const buyTokenParam = currency ? getZeroExTokenParam(currency) : undefined

  const queryEnabled = Boolean(!currencyIsStablecoin && sellTokenParam && buyTokenParam && sellAmountRaw)

  const normalizedPollInterval = pollInterval > 0 ? Math.max(pollInterval, ZERO_EX_PRICE_STALE_TIME_MS) : undefined

  const { data: quoteData, isLoading: queryLoading } = useQuery<ZeroExPriceQuote | null>({
    queryKey: ['zero-ex-usdc-price', sellTokenParam, buyTokenParam, sellAmountRaw],
    enabled: queryEnabled,
    queryFn: async ({ signal }) => {
      if (!sellTokenParam || !buyTokenParam || !sellAmountRaw) {
        return null
      }

      const params = new URLSearchParams({
        sellToken: sellTokenParam,
        buyToken: buyTokenParam,
        sellAmount: sellAmountRaw,
      })

      const response = await fetch(`${ZERO_EX_PRICE_URL}?${params.toString()}`, {
        method: 'GET',
        signal,
      })

      if (!response.ok) {
        return null
      }

      const data = (await response.json()) as ZeroExPriceResponse
      const buyAmountRaw = data.buyAmount
      const sellAmountResponse = data.sellAmount ?? sellAmountRaw

      if (!buyAmountRaw) {
        return null
      }

      return {
        buyAmountRaw,
        sellAmountRaw: sellAmountResponse,
      }
    },
    staleTime: ZERO_EX_PRICE_STALE_TIME_MS,
    gcTime: ZERO_EX_PRICE_GC_TIME_MS,
    refetchInterval: queryEnabled && normalizedPollInterval ? normalizedPollInterval : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  const price = useMemo(() => {
    if (currencyIsStablecoin && stablecoin) {
      return new Price(stablecoin, stablecoin, '1', '1')
    }

    if (!currency || !stablecoin || !quoteData?.buyAmountRaw || !quoteData.sellAmountRaw) {
      return undefined
    }

    try {
      const buyAmount = JSBI.BigInt(quoteData.buyAmountRaw)
      const sellAmount = JSBI.BigInt(quoteData.sellAmountRaw)
      const zero = JSBI.BigInt(0)

      if (JSBI.greaterThan(buyAmount, zero) && JSBI.greaterThan(sellAmount, zero)) {
        return new Price(currency, stablecoin, buyAmount, sellAmount)
      }
    } catch (error) {
      return undefined
    }

    return undefined
  }, [currency, stablecoin, currencyIsStablecoin, quoteData])

  const isLoading = currencyIsStablecoin ? false : queryEnabled ? queryLoading : false

  return { price, isLoading }
}

export function useUSDCValue(
  currencyAmount: CurrencyAmount<Currency> | undefined | null,
): CurrencyAmount<Currency> | null {
  const { price } = useUSDCPrice(currencyAmount?.currency)

  return useMemo(() => {
    if (!price || !currencyAmount) {
      return null
    }
    try {
      return price.quote(currencyAmount)
    } catch (error) {
      return null
    }
  }, [currencyAmount, price])
}

/**
 * @param currencyAmount
 * @returns Returns fiat value of the currency amount, and loading status of the currency<->stable quote
 */
export function useUSDCValueWithStatus(currencyAmount: CurrencyAmount<Currency> | undefined | null): {
  value: CurrencyAmount<Currency> | null
  isLoading: boolean
} {
  const { price, isLoading } = useUSDCPrice(currencyAmount?.currency)

  return useMemo(() => {
    if (!price || !currencyAmount) {
      return { value: null, isLoading }
    }
    try {
      return { value: price.quote(currencyAmount), isLoading }
    } catch (error) {
      return {
        value: null,
        isLoading: false,
      }
    }
  }, [currencyAmount, isLoading, price])
}
